import { APP_BASE_HREF } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { createSelector, Store } from '@ngrx/store';
import { slashJoin } from 'path-slashes';
import { Observable, throwError } from 'rxjs';
import { catchError, first, map, switchMap, tap } from 'rxjs/operators';

import { PrintInput } from 'src/app/api/kevin/models';
import { PdfPrintService } from 'src/app/api/kevin/services';
import { Vehicle } from 'src/app/api/owcc/models';
import { appInject } from 'src/app/app-injector';
import { AppRoutePath } from 'src/app/routing/app-route-path.enum';
import { selectApiContext } from 'src/app/select-api-context';
import { NotificationService } from 'src/app/shared/notification/notification.service';
import { translate } from 'src/app/shared/pipes/translate/translate.pipe';
import { toggleLoadingSpinner } from 'src/app/store/common/common.actions';
import { selectConfigurationVehicle } from 'src/app/store/common/common.selectors';
import { selectRouteState } from 'src/app/store/router/router.selectors';
import { selectOnlineCode } from 'src/app/store/runtime-variables/runtime-variables.selectors';
import { TRACKING_PARAM_DIRECT_LINK } from 'src/app/utils/global-constants';
import { WINDOW } from 'src/app/utils/window.token';

import { GENERATE_PDF_ERROR_TRACKING_DATA } from '../../app-error/notification-tracking';
import { TrackingService } from '../tracking/tracking.service';

interface Pdf {
  filename: string;
  blob: Blob;
}

const selectOnlineCodeLink = createSelector(
  selectOnlineCode,
  selectRouteState,
  (onlineCode, routeState): string | undefined => {
    if (!onlineCode) return undefined;
    const path = [
      AppRoutePath.OnlineCode,
      routeState?.pathParams?.subprocess,
      routeState?.pathParams?.country,
      routeState?.pathParams?.language,
      onlineCode
    ];
    const baseHref = appInject(APP_BASE_HREF);
    const win = appInject(WINDOW);
    return `${slashJoin(win.location.origin, baseHref, path)}?${TRACKING_PARAM_DIRECT_LINK}`;
  }
);

const selectPdfGenerationData = createSelector(selectOnlineCode, selectConfigurationVehicle, (onlineCode, vehicle) => ({
  onlineCode,
  vehicle
}));

const selectPdfParams = createSelector(selectOnlineCodeLink, selectApiContext, (onlineCodeAndLink, apiContext) => ({
  link: onlineCodeAndLink,
  apiContext
}));

@Injectable({
  providedIn: 'root'
})
export class PrintService {
  public constructor(
    private readonly notificationService: NotificationService,
    private readonly pdfPrintService: PdfPrintService,
    private readonly store: Store,
    private readonly trackingService: TrackingService,
    @Inject(WINDOW) private readonly window: Window
  ) {}

  public print(): void {
    const raiseNotificationOnError = (): void => {
      this.notificationService.notify({
        content: translate('print.error'),
        variant: 'error',
        dismissable: true,
        autoDismiss: true,
        icon: 'alert-circle'
      });

      // Send tracking data
      this.trackingService.sendTrackingData(GENERATE_PDF_ERROR_TRACKING_DATA);
    };

    const getPdf = (onlineCode: string, vehicle: Vehicle): Observable<Pdf> =>
      this.store.select(selectPdfParams).pipe(
        first(),
        switchMap(({ link, apiContext }) => {
          const body: PrintInput = {
            onlineCode: {
              code: onlineCode,
              link
            },
            timeZoneOffset: new Date().getTimezoneOffset()
          };

          return this.pdfPrintService
            .generatePdf({
              market: apiContext.market,
              subProcess: apiContext.subProcess || '',
              dataVersion: apiContext.dataVersion || '',
              vehicleId: vehicle.vehicleId || '',
              body
            })
            .pipe(
              catchError((): Observable<never> => {
                raiseNotificationOnError();
                return throwError(() => new Error('error downloading pdf'));
              }),
              map(
                (blob: Blob): Pdf => ({
                  blob,
                  filename: `${vehicle?.name?.replaceAll(/[\s.|]+/g, '_')}_${onlineCode}`
                })
              )
            );
        })
      );

    const updateSpinner = (isLoading: boolean): void => {
      this.store.dispatch(toggleLoadingSpinner({ isLoading }));
    };

    this.store
      .select(selectPdfGenerationData)
      .pipe(
        tap(() => {
          updateSpinner(true);
        }),
        first(),
        switchMap(({ onlineCode, vehicle }) => getPdf(onlineCode || '', vehicle || ({} as Vehicle)))
      )
      .subscribe({
        next: (pdf: Pdf) => {
          updateSpinner(false);
          this.download(pdf);
        },
        error: () => {
          updateSpinner(false);
        }
      });
  }

  private download(pdf: Pdf): void {
    const a = this.window.document.createElement('a');
    a.href = URL.createObjectURL(pdf.blob);
    a.target = '_blank';
    a.download = pdf.filename;
    a.click();
  }
}
